import type { ApiClient, Schemas } from '@argzombie/api-client';
import { ok } from './api';

/*
 * The Outta-Ten admin calls (ADR 0066, CONTRACT §7), all through the generated client.
 *
 * | Function | Endpoint |
 * |---|---|
 * | `playerSheets` | `GET /admin/players/{id}/skills` (every member's SheetView; null on a 404 = an older API) |
 * | `explainSkill` | `GET /dev/skills/explain?playerId&memberId&key` (the evaluator's trace, the Effects explain shape) |
 * | `playtestMetrics` | `GET /admin/playtest/metrics?days` (`skills` is null until a run has a sheet) |
 */

export type PlayerSheets = Schemas['AdminSheetsResponse'];
export type MemberSheet = Schemas['AdminSheetMember'];
export type SheetView = Schemas['SheetView'];
/** shared-cs `AbilityView` (CONTRACT §13.4): a milestone at 5, 7 or 10, active while the base level is at or above it. */
export type AbilityView = { at?: number; name?: string; text?: string; active?: boolean };
/**
 * `SheetEntryView` plus the round 2 fields (`abilities[]`, `info`), typed locally until the client is regenerated (C2). Perks are derived
 * from levels now (no picks, no pending forks): the admin reads the active ones from `abilities[]`.
 */
export type SheetEntryView = Schemas['SheetEntryView'] & {
  abilities?: AbilityView[] | null;
  info?: { affects?: string; train?: string } | null;
};
export type SkillExplain = Schemas['EffectExplain'];
export type PlaytestMetrics = Schemas['PlaytestMetricsResponse'];
export type SkillMetrics = Schemas['SkillMetricsView'];

/** A player's sheets, or null when the API answers 404 (a server from before ADR 0066). */
export const playerSheets = async (client: ApiClient, playerId: string) => {
  const r = await client.GET('/admin/players/{id}/skills', { params: { path: { id: playerId } } });
  if (r.data === undefined && r.response.status === 404) return null;
  return ok(r);
};

export const explainSkill = async (
  client: ApiClient,
  playerId: string,
  memberId: string,
  key: string,
) =>
  ok(
    await client.GET('/dev/skills/explain', {
      params: { query: { playerId, memberId, key } },
    }),
  );

export const playtestMetrics = async (client: ApiClient, days: number) =>
  ok(await client.GET('/admin/playtest/metrics', { params: { query: { days } } }));

/** "3.4" for a number, "-" for null/absent. */
export const fmt = (v: number | null | undefined, digits = 1) =>
  v == null || Number.isNaN(v) ? '-' : Number.isInteger(v) ? String(v) : v.toFixed(digits);

/** "7 Follow Through, 5 Heavy Hands": the milestones live at the entry's base level (CONTRACT §13.4: perks derive from levels). */
export const activeAbilities = (r: SheetEntryView) =>
  (r.abilities ?? [])
    .filter((a) => a?.active)
    .map((a) => `${a.at ?? ''} ${a.name ?? ''}`.trim())
    .join(', ');
